import styled from 'styled-components';
import { Card } from '@shared/ui/Card';

export const Layout = styled.div`
  display: grid;
  /* minmax(0, …): длинный заголовок обработки (в одну строку с многоточием)
     иначе распирает колонку за край экрана. */
  grid-template-columns: 380px minmax(0, 1fr);
  gap: ${({ theme }) => theme.spacing(5)};
  align-items: start;

  /* Слева меню приложения и МТО — на ноутбуке форме и списку рядом тесно. */
  @media (max-width: 1280px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(4)};
`;

export const FormTitle = styled.h3`
  margin: 0 0 ${({ theme }) => theme.spacing(4)};
  font-size: 15px;
  font-weight: 600;
`;

export const FileHint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-top: ${({ theme }) => theme.spacing(1)};
`;

/** Подсказка вместо зоны загрузки, когда права на запись нет: молча
 *  недоступная зона выглядит как поломка (см. `MtoDashboard`). */
export const ReadOnlyHint = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.muted};
  padding: ${({ theme }) => `${theme.spacing(4)} 0`};
`;

export const FileList = styled.ul`
  list-style: none;
  margin: ${({ theme }) => theme.spacing(3)} 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
`;

export const FileItem = styled.li`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing(3)};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

export const FileRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  min-width: 0;
`;

export const FileName = styled.span`
  flex: 1;
  min-width: 0;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  & > svg {
    vertical-align: -2px;
    margin-right: ${({ theme }) => theme.spacing(1.5)};
    color: ${({ theme }) => theme.colors.text.muted};
  }
`;

export const FileSize = styled.span`
  flex-shrink: 0;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.colors.text.muted};
`;

export const RemoveBtn = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: transparent;
  color: ${({ theme }) => theme.colors.text.muted};
  cursor: pointer;
  transition:
    color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    background ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover {
    color: ${({ theme }) => theme.colors.status.danger};
    background: ${({ theme }) => theme.colors.surfaceHover};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

export const TypeHint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

export const Err = styled.div`
  color: ${({ theme }) => theme.colors.status.danger};
  font-size: 13px;
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

export const ListHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
`;

export const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(5)};
`;

export const ScopeSwitch = styled.div`
  display: inline-flex;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.pill};
  overflow: hidden;
`;

export const ScopeBtn = styled.button<{ $active: boolean }>`
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(3)}`};
  border: none;
  background: ${({ $active, theme }) => ($active ? theme.colors.primarySoft : 'transparent')};
  color: ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.text.secondary)};
  cursor: pointer;

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

export const Muted = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

export const RunCard = styled(Card)`
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing(4)};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};

  /* Карточка кликабельна (role="button"): свой focus-ring с радиусом карточки.
     Глобальное правило ставит мелкий radius.xs — переопределяем под lg. */
  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
    border-radius: ${({ theme }) => theme.radius.lg};
  }
`;

export const RunTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
`;

export const RunTitle = styled.div`
  font-weight: 700;
  font-size: 15px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const RunMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  flex-wrap: wrap;
`;

export const OpenHint = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  margin-left: auto;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 600;

  & > svg {
    transition: transform ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.spring};
  }
  ${RunCard}:hover & > svg {
    transform: translateX(3px);
  }
`;

export const EmptyState = styled(Card)`
  text-align: center;
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: 14px;
  padding: ${({ theme }) => theme.spacing(8)} ${({ theme }) => theme.spacing(4)};
`;

/** Бейдж модуля в карточке обработки — показываем только в сводном реестре «Все
 *  обработки» (в раздел модуля он избыточен — все обработки одного модуля). */
export const ModuleTag = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 600;
  padding: ${({ theme }) => `${theme.spacing(0.5)} ${theme.spacing(2)}`};
  border-radius: ${({ theme }) => theme.radius.pill};
  border: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.text.secondary};
  white-space: nowrap;
`;
